import {StockLevelOverwrite} from '@domain/material-storage'
import {roundTo15MinIntervalStart} from '@hconnect/common/utils'
import {datetimeFormatter, formatTons} from '@hconnect/uikit'
import {CardTitle} from '@hconnect/uikit/src/lib2'
import {useUpmPlantTimezone} from '@hconnect/upm'
import {InfoOutlined} from '@mui/icons-material'
import {Typography} from '@mui/material'
import {Stack} from '@mui/system'
import moment from 'moment-timezone'
import React from 'react'
import {useTranslation} from 'react-i18next'

import {STOCK_CARD_WIDGET_NAMESPACE} from '../consts'

import {useCurrentTimeRounded} from './hooks/useCurrentTimeRounded'
import {DatetimeValue} from './types'

interface StockDevelopmentTooltipContentProps {
  datetimeValue: DatetimeValue
  canCreateStockOverwrite: boolean
  stockOverwrites: StockLevelOverwrite[]
}

export const StockDevelopmentTooltipContent: React.FC<StockDevelopmentTooltipContentProps> = ({
  canCreateStockOverwrite,
  datetimeValue: {value, datetime},
  stockOverwrites
}) => {
  const timezoneId = useUpmPlantTimezone()

  const {
    t,
    i18n: {language}
  } = useTranslation(STOCK_CARD_WIDGET_NAMESPACE)

  const currentDateTime = useCurrentTimeRounded({timezoneId, roundingFn: roundTo15MinIntervalStart})
  const hoveredDatetime = moment.utc(datetime).tz(timezoneId)

  const dateTimeMoment = moment.utc(datetime).tz(timezoneId)

  const overwrite = stockOverwrites.find((o) =>
    moment.utc(o.measuredAt).tz(timezoneId).isSame(dateTimeMoment)
  )

  const showStockOverwriteTooltip =
    canCreateStockOverwrite && hoveredDatetime.isSameOrBefore(currentDateTime)

  return (
    <>
      <CardTitle>
        {datetimeFormatter(hoveredDatetime, language, hoveredDatetime.utcOffset())}
      </CardTitle>
      <Stack spacing={2}>
        <Stack direction="row" justifyContent="space-between">
          <Typography variant="body1">{t('stockDevelopmentChart.stock')}</Typography>
          <Typography variant="subtitle1">{formatTons(value, language)}</Typography>
        </Stack>
        {overwrite && (
          <Typography variant="subtitle1">
            {t('stockDevelopmentChart.lastCorrectionBy', {name: overwrite.updatedBy})}
          </Typography>
        )}
        {showStockOverwriteTooltip && !overwrite && (
          <Typography variant="subtitle1">
            <InfoOutlined
              sx={{
                mr: 0.5,
                verticalAlign: 'middle',
                fontSize: 'inherit'
              }}
            />
            {t('stockDevelopmentChart.clickToCorrectStockLevel')}
          </Typography>
        )}
      </Stack>
    </>
  )
}
